import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  PlusCircle,
  Link2,
  BarChart3,
  Key,
  TestTube,
} from "lucide-react";
import { useAuth } from "@/features/auth";
import { cn } from "@/lib/utils";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  roles?: Array<"Admin" | "Marketing" | "CustomerSuccess">;
}

const navItems: NavItem[] = [
  { label: "Dashboard", to: "/", icon: LayoutDashboard },
  { label: "Create Link", to: "/create", icon: PlusCircle },
  { label: "Links", to: "/links", icon: Link2 },
  { label: "Analytics", to: "/analytics", icon: BarChart3 },
  { label: "API Keys", to: "/api-keys", icon: Key, roles: ["Admin"] },
  { label: "Test Console", to: "/test-console", icon: TestTube },
];

export function Sidebar({ open, onClose }: SidebarProps) {
  const { hasAnyRole } = useAuth();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const visibleItems = navItems.filter(
    (item) => !item.roles || hasAnyRole(...item.roles),
  );

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 border-r border-gray-200 bg-white pt-16 transition-transform lg:static lg:translate-x-0 dark:border-gray-700 dark:bg-gray-800",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <nav className="flex flex-col gap-1 p-4">
          {visibleItems.map((item) => {
            const isActive =
              currentPath === item.to ||
              (item.to !== "/" && currentPath.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700",
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
